// Baking a gantt for the HTML export (TODO §26/§9.2).
//
// Every other export path renders the gantt live: presentation, the print layer and the PDF
// all mount `<Slide readOnly>`, and a read-only SVAR chart is ordinary same-origin DOM that
// prints crisply. The **single-file HTML export cannot**: it is a string of markup with no
// runtime dependency (§6.6), and shipping a React widget plus 150KB of vendor CSS into it
// would make it neither single nor offline. So it gets a raster, exactly as a Gridd does —
// the deliberate decision that the HTML export carries snapshots rather than live embeds.
//
// Two rules inherited from `collectDiagramSvgs`, and both matter:
//   • it runs off the STORE's bodies, never the on-screen widget, so an export does not
//     depend on a slide having been scrolled into view;
//   • one unrenderable schedule costs that window, not the export.

import { createRoot } from "react-dom/client";
import { domToPng } from "modern-screenshot";

import { ganttFontPx, resolveGanttTheme } from "@/lib/gantt";
import { sheetSwatches } from "@/lib/stylesheet-typography";
import { loadGanttCanvas } from "@/lib/gantt-engine";
import type { GriddStyleSheetData, SlideData } from "@/types/document";
import type { GanttDocument } from "@/types/gantt";

// 2×, matching the slide thumbnails — a gantt is mostly hairlines and small type, and the
// exported file is viewed full-screen.
const CAPTURE_SCALE = 2;

// The box each chart is authored into for the bake. It is an APPROXIMATION of the content
// window's size inside the exported 1600×900 canvas (`.content` is flex:1 under the title
// rows, `.frame` takes a 12px gap between two windows), and it is allowed to be: the image is
// fitted into whatever the frame turns out to be with `object-fit: contain`, and the chart
// itself re-fits to the box it is given. An aspect that differs by a few percent letterboxes
// by a few pixels — the §21/§3.4 rule, applied to a raster.
const WINDOW_W = 1560;
const WINDOW_H = 600;
const WINDOW_GAP = 12;

function windowBox(
  slide: SlideData,
  index: number,
): { width: number; height: number } {
  const ratio = slide.splitRatio ?? 0.5;
  const share = index === 0 ? ratio : 1 - ratio;
  if (slide.layout === "two-h") {
    return { width: (WINDOW_W - WINDOW_GAP) * share, height: WINDOW_H };
  }
  if (slide.layout === "two-v") {
    return { width: WINDOW_W, height: (WINDOW_H - WINDOW_GAP) * share };
  }
  return { width: WINDOW_W, height: WINDOW_H };
}

// Let the widget mount, measure and paint before the camera fires. Two animation frames plus
// a tick is the same shape of wait the thumbnail capturer relies on; without it the PNG
// catches an empty chart and nothing would ever invalidate it.
function settle(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setTimeout(resolve, 0)),
    );
  });
}

async function bakeOne(
  doc: GanttDocument,
  box: { width: number; height: number },
  styleSheet: GriddStyleSheetData,
): Promise<string | null> {
  const { default: GanttCanvas } = await loadGanttCanvas();
  const host = document.createElement("div");
  // Off-screen but IN the document: an element in no document has no layout, so the widget
  // would measure zero and render nothing. Fixed rather than absolute so it cannot extend the
  // page's scroll area while it is there.
  host.style.cssText = `position:fixed;left:-100000px;top:0;width:${box.width}px;height:${box.height}px;background:#fff;`;
  document.body.appendChild(host);
  const root = createRoot(host);
  try {
    root.render(
      <GanttCanvas
        doc={doc}
        box={box}
        readOnly
        theme={resolveGanttTheme(styleSheet)}
        swatches={sheetSwatches(styleSheet)}
        // The bake is authored in the slide's logical units, so no counter-transform scale.
        fontPx={ganttFontPx(styleSheet)}
      />,
    );
    await settle();
    return await domToPng(host, {
      scale: CAPTURE_SCALE,
      backgroundColor: "#ffffff",
    });
  } catch (err) {
    console.warn("[htmlExport] gantt bake failed", err);
    return null;
  } finally {
    // Unmount out of band: React refuses to unmount a root synchronously from inside a
    // render pass, and this can be called from one.
    setTimeout(() => {
      root.unmount();
      host.remove();
    }, 0);
  }
}

// ganttDocId → a PNG data URL. Absent entries render an empty frame, exactly as a missing
// grid snapshot does.
export async function collectGanttImages(
  slides: SlideData[],
  docs: Record<string, GanttDocument>,
  styleSheet: GriddStyleSheetData,
): Promise<Record<string, string>> {
  const targets = new Map<string, { width: number; height: number }>();
  for (const slide of slides) {
    slide.contents.forEach((content, index) => {
      if (content?.kind === "gantt" && !targets.has(content.ganttDocId)) {
        targets.set(content.ganttDocId, windowBox(slide, index));
      }
    });
  }
  const out: Record<string, string> = {};
  // Sequential, unlike the diagram pass: each bake puts a live widget in the document and
  // rasterizes it, and several at once would contend for layout with each other.
  for (const [docId, box] of targets) {
    const doc = docs[docId];
    if (!doc) continue;
    const png = await bakeOne(doc, box, styleSheet);
    if (png) out[docId] = png;
  }
  return out;
}
